import { Component, OnInit } from "@angular/core";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { authenticationService } from "@app/login/auth.service";
import { clientService } from "@app/client/shared/client.service";
import { Permissions } from "@models/permissions.enum";
import { SchedulingTeam } from "@app/shared/models/Board.model";

const NO_CLIENT = "__no_client__";

@Component({
  selector: "app-scheduling-teams",
  templateUrl: "./scheduling-teams.component.html",
  styleUrls: ["./scheduling-teams.component.css"],
})
export class SchedulingTeamsComponent implements OnInit {
  schedulingTeams: SchedulingTeam[] = [];
  filteredSchedulingTeams: SchedulingTeam[] = [];
  expandedSchedulingTeamIds: Set<string> = new Set();
  isLoading = false;
  searchText = "";

  // Email selection
  selectedTeamIds: Set<string> = new Set();
  selectedClientFilter: string | null = null;
  showEmailModal = false;
  emailCompose = { to: "", cc: "", subject: "", body: "" };
  emailValidationErrors: { to: string; cc: string; subject: string; body: string } = {
    to: "",
    cc: "",
    subject: "",
    body: "",
  };
  emailAttachments: File[] = [];
  isSendingEmail = false;

  showAddModal = false;
  showEditModal = false;

  newSchedulingTeam: SchedulingTeam = this.createEmptySchedulingTeam();
  editingSchedulingTeam: SchedulingTeam | null = null;

  selectedBoardNames: string[] = [""];
  selectedContacts: {
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    doNotContact: boolean;
  }[] = [];
  editSelectedBoardNames: string[] = [];
  editSelectedContacts: {
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    doNotContact: boolean;
  }[] = [];

  /** Per-row validation errors for the contact forms (keyed by row index). */
  newContactErrors: { [index: number]: string } = {};
  editContactErrors: { [index: number]: string } = {};

  clients: { id: string; companyName: string }[] = [];
  selectedClientId: string | null = null;

  canEdit = false;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private authService: authenticationService,
    private clientService: clientService
  ) {}

  ngOnInit() {
    this.canEdit = this.hasPermission("BoardsChange");
    this.loadSchedulingTeams();
    this.loadClients();
  }

  hasPermission(name: string): boolean {
    const perm = (Permissions as any)[name];
    return this.authService.hasPermission(perm);
  }

  createEmptySchedulingTeam(): SchedulingTeam {
    return { name: "", contacts: [], boards: [], drivers: [], membersAreNonDrivers: false };
  }

  loadSchedulingTeams() {
    this.isLoading = true;
    this.http.get("api/board/listAll").subscribe({
      next: (teams: any) => {
        this.schedulingTeams = teams || [];
        this.filterSchedulingTeams();
        this.isLoading = false;
      },
      error: (err) => {
        this.toastr.error("Error loading scheduling teams");
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  loadClients() {
    this.clientService.clientListDropdown().subscribe({
      next: (clients: any) => {
        this.clients = (clients || []).filter((c: any) => !c.clientId);
      },
      error: (err) => console.error("Error loading clients", err),
    });
  }

  filterSchedulingTeams() {
    const q = (this.searchText || "").toLowerCase().trim();
    if (!q) {
      this.filteredSchedulingTeams = [...this.schedulingTeams];
      return;
    }
    this.filteredSchedulingTeams = this.schedulingTeams.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.clientName || "").toLowerCase().includes(q) ||
        (t.contacts || []).some(
          (c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q) ||
            (c.role || "").toLowerCase().includes(q)
        ) ||
        (t.boards || []).some((b) => (b.name || "").toLowerCase().includes(q)) ||
        (t.drivers || []).some(
          (d) =>
            (d.name || "").toLowerCase().includes(q) ||
            (d.lastName || "").toLowerCase().includes(q) ||
            (d.driverNo || "").toLowerCase().includes(q) ||
            (d.boardName || "").toLowerCase().includes(q)
        )
    );
  }

  toggleSchedulingTeamExpansion(id: string) {
    this.expandedSchedulingTeamIds.has(id)
      ? this.expandedSchedulingTeamIds.delete(id)
      : this.expandedSchedulingTeamIds.add(id);
  }

  isSchedulingTeamExpanded(id: string): boolean {
    return this.expandedSchedulingTeamIds.has(id);
  }

  // ── Team selection (for emailing) ──

  toggleSelectTeam(id: string) {
    if (this.selectedTeamIds.has(id)) this.selectedTeamIds.delete(id);
    else this.selectedTeamIds.add(id);
  }

  isTeamSelected(id: string): boolean {
    return this.selectedTeamIds.has(id);
  }

  clearTeamSelection() {
    this.selectedTeamIds.clear();
  }

  get clientOptions(): { id: string; name: string }[] {
    const map = new Map<string, { id: string; name: string }>();
    for (const team of this.schedulingTeams) {
      const id = team.clientId || NO_CLIENT;
      const name = team.clientName || "No Client";
      if (!map.has(name)) {
        map.set(name, { id, name });
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  onSelectAllByClient(clientId: string) {
    if (!clientId) return;
    const targetId = clientId === NO_CLIENT ? null : clientId;
    const clientName = this.clientOptions.find((c) => c.id === clientId)?.name || "No Client";
    let count = 0;
    this.schedulingTeams.forEach((t) => {
      const teamClientId = t.clientId || null;
      if (teamClientId === targetId && t.id) {
        this.selectedTeamIds.add(t.id);
        count++;
      }
    });
    this.selectedClientFilter = null;
    if (count > 0) {
      this.toastr.success(
        `Selected ${count} scheduling team${count !== 1 ? "s" : ""} for ${clientName}`
      );
    }
  }

  get emailDriverCount(): number {
    return this.collectDriverEmails(
      this.schedulingTeams.filter((t) => t.id && this.selectedTeamIds.has(t.id))
    ).length;
  }

  // ── Email drivers on selected teams ──

  openEmailModal() {
    if (this.selectedTeamIds.size === 0) {
      this.toastr.warning("No scheduling teams selected");
      return;
    }
    const selectedTeams = this.schedulingTeams.filter(
      (t) => t.id && this.selectedTeamIds.has(t.id)
    );
    if (selectedTeams.length === 0) {
      this.toastr.warning("No scheduling teams selected");
      return;
    }

    const teamNames = selectedTeams
      .map((t) => t.name)
      .filter(Boolean)
      .join(", ");
    this.emailCompose = {
      to: this.collectDriverEmails(selectedTeams).join("; "),
      cc: "",
      subject: ``,
      body: ``,
    };
    this.emailValidationErrors = { to: "", cc: "", subject: "", body: "" };
    this.emailAttachments = [];
    this.showEmailModal = true;
  }

  cancelEmailModal() {
    this.showEmailModal = false;
    this.emailCompose = { to: "", cc: "", subject: "", body: "" };
    this.emailValidationErrors = { to: "", cc: "", subject: "", body: "" };
    this.emailAttachments = [];
  }

  async confirmSendEmail() {
    if (this.isSendingEmail) return;
    if (!this.validateEmailFields()) return;
    if (!this.emailCompose.to || !this.emailCompose.subject || !this.emailCompose.body) return;

    this.isSendingEmail = true;
    try {
      const attachmentPayload = [];
      for (const file of this.emailAttachments) {
        attachmentPayload.push(await this.readFileAsBase64(file));
      }

      const toList = this.emailCompose.to
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter((e) => e);

      this.http
        .post("api/board/sendEmails", {
          to: toList,
          cc: this.emailCompose.cc ?? "",
          subject: this.emailCompose.subject,
          body: this.emailCompose.body,
          attachments: attachmentPayload,
        })
        .subscribe({
          next: (result: any) => {
            this.isSendingEmail = false;
            this.toastr.success(
              `Emails sent: ${result.sentCount} succeeded, ${result.failCount} failed out of ${result.total}.`
            );
            this.cancelEmailModal();
            this.clearTeamSelection();
          },
          error: (err) => {
            this.isSendingEmail = false;
            this.toastr.error(err.error || "Failed to send emails");
          },
        });
    } catch (err) {
      this.isSendingEmail = false;
      this.toastr.error("Failed to process attachments");
      console.error(err);
    }
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this.emailAttachments.push(input.files[i]);
      }
    }
    input.value = "";
  }

  removeAttachment(index: number) {
    this.emailAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  clearEmailValidationError(field: "to" | "cc" | "subject" | "body") {
    this.emailValidationErrors[field] = "";
  }

  isValidEmail(email: string): boolean {
    if (!email || !email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  validateEmailFields(): boolean {
    this.emailValidationErrors = { to: "", cc: "", subject: "", body: "" };
    let isValid = true;

    const toEmails = this.emailCompose.to
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter((e) => e);
    if (toEmails.length === 0) {
      this.emailValidationErrors.to = "At least one recipient email is required.";
      isValid = false;
    } else {
      for (const email of toEmails) {
        if (!this.isValidEmail(email)) {
          this.emailValidationErrors.to = `Invalid email: ${email}`;
          isValid = false;
          break;
        }
      }
    }

    if (this.emailCompose.cc && this.emailCompose.cc.trim()) {
      const ccEmails = this.emailCompose.cc
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter((e) => e);
      for (const cc of ccEmails) {
        if (!this.isValidEmail(cc)) {
          this.emailValidationErrors.cc = `Invalid email: ${cc}`;
          isValid = false;
          break;
        }
      }
    }

    if (!this.emailCompose.subject || !this.emailCompose.subject.trim()) {
      this.emailValidationErrors.subject = "Subject is required.";
      isValid = false;
    }

    if (!this.emailCompose.body || !this.emailCompose.body.trim()) {
      this.emailValidationErrors.body = "Message is required.";
      isValid = false;
    }

    return isValid;
  }

  private collectDriverEmails(teams: SchedulingTeam[]): string[] {
    const seen = new Set<string>();
    const emails: string[] = [];
    for (const team of teams) {
      for (const d of team.drivers || []) {
        const email = (d.email || "").trim();
        if (email && !seen.has(email.toLowerCase())) {
          seen.add(email.toLowerCase());
          emails.push(email);
        }
      }
    }
    return emails;
  }

  private readFileAsBase64(
    file: File
  ): Promise<{ fileName: string; contentBase64: string; contentType: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1] || "";
        resolve({
          fileName: file.name,
          contentBase64: base64,
          contentType: file.type || "application/octet-stream",
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ── Add modal ──

  openAddModal() {
    this.newSchedulingTeam = this.createEmptySchedulingTeam();
    this.selectedClientId = null;
    this.selectedBoardNames = [""];
    this.selectedContacts = [];
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
    this.newSchedulingTeam = this.createEmptySchedulingTeam();
    this.selectedBoardNames = [];
    this.selectedContacts = [];
  }

  createSchedulingTeam() {
    if (!this.newSchedulingTeam.name?.trim()) {
      this.toastr.warning("Scheduling team name is required");
      return;
    }
    if (!this.validateContacts(this.selectedContacts, this.newContactErrors)) return;

    const validBoardNames = this.selectedBoardNames.filter((n) => n?.trim());
    const validContacts = this.selectedContacts.filter((c) => c.name?.trim());
    this.http
      .post("api/board/create", {
        name: this.newSchedulingTeam.name.trim(),
        clientId: this.selectedClientId || null,
        membersAreNonDrivers: !!this.newSchedulingTeam.membersAreNonDrivers,
        boardNames: validBoardNames,
        contacts: validContacts,
        driverAssignments: [],
      })
      .subscribe({
        next: () => {
          this.toastr.success("Scheduling team created successfully");
          this.closeAddModal();
          this.loadSchedulingTeams();
        },
        error: (err) => {
          this.toastr.error(err || "Error creating scheduling team");
          console.error(err);
        },
      });
  }

  // ── Edit modal ──

  openEditModal(team: SchedulingTeam) {
    this.editingSchedulingTeam = { ...team };
    this.editSelectedBoardNames = (team.boards || []).map((b) => b.name);
    this.editSelectedContacts = (team.contacts || []).map((c) => ({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      role: c.role || "",
      doNotContact: c.doNotContact || false,
    }));
    if (this.editSelectedContacts.length === 0)
      this.editSelectedContacts.push({
        name: "",
        email: "",
        phone: "",
        role: "",
        doNotContact: false,
      });
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingSchedulingTeam = null;
    this.editSelectedBoardNames = [];
    this.editSelectedContacts = [];
  }

  updateSchedulingTeam() {
    if (!this.editingSchedulingTeam) return;
    if (!this.editingSchedulingTeam.name?.trim()) {
      this.toastr.warning("Scheduling team name is required");
      return;
    }
    if (!this.validateContacts(this.editSelectedContacts, this.editContactErrors)) return;

    const validBoardNames = this.editSelectedBoardNames.filter((n) => n?.trim());
    const validContacts = this.editSelectedContacts.filter((c) => c.name?.trim());
    const existingDriverAssignments = this.editingSchedulingTeam.drivers.map((d) => ({
      driverId: d.driverId,
      boardName: d.boardName,
      billToClientId: d.billToClientId,
    }));
    this.http
      .patch("api/board/update", {
        id: this.editingSchedulingTeam.id,
        name: this.editingSchedulingTeam.name.trim(),
        membersAreNonDrivers: !!this.editingSchedulingTeam.membersAreNonDrivers,
        boardNames: validBoardNames,
        contacts: validContacts,
        driverAssignments: existingDriverAssignments,
      })
      .subscribe({
        next: () => {
          this.toastr.success("Scheduling team updated successfully");
          this.closeEditModal();
          this.loadSchedulingTeams();
        },
        error: (err) => {
          this.toastr.error(err || "Error updating scheduling team");
          console.error(err);
        },
      });
  }

  // ── Delete ──

  deleteSchedulingTeam(team: SchedulingTeam) {
    if (!confirm(`Are you sure you want to delete the scheduling team "${team.name}"?`)) return;
    this.http.delete(`api/board/delete/${team.id}`).subscribe({
      next: () => {
        this.toastr.success("Scheduling team deleted successfully");
        this.loadSchedulingTeams();
      },
      error: (err) => {
        this.toastr.error(err || "Error deleting scheduling team");
        console.error(err);
      },
    });
  }

  // ── Board helpers ──

  addNewBoard() {
    this.selectedBoardNames.push("");
  }
  removeNewBoard(index: number) {
    this.selectedBoardNames.splice(index, 1);
  }
  addEditBoard() {
    this.editSelectedBoardNames.push("");
  }
  removeEditBoard(index: number) {
    this.editSelectedBoardNames.splice(index, 1);
  }

  // ── Contact helpers ──

  /**
   * Contact rows need a name. A row that has other details filled in but no name would be dropped by the
   * save (contacts without a name are not created), so block the save and flag the row instead.
   * Fully blank rows are ignored.
   */
  private validateContacts(
    contacts: {
      name?: string;
      email?: string;
      phone?: string;
      role?: string;
      doNotContact?: boolean;
    }[],
    errors: { [index: number]: string }
  ): boolean {
    Object.keys(errors).forEach((key) => delete errors[+key]);

    let isValid = true;
    contacts.forEach((contact, index) => {
      const hasContent = !!(
        contact.name?.trim() ||
        contact.email?.trim() ||
        contact.phone?.trim() ||
        contact.role?.trim() ||
        contact.doNotContact
      );

      if (hasContent && !contact.name?.trim()) {
        errors[index] = "Contact name is required.";
        isValid = false;
      }
    });

    if (!isValid) {
      this.toastr.warning("Contact name is required");
    }

    return isValid;
  }

  clearContactNameError(index: number, mode: "new" | "edit") {
    const errors = mode === "new" ? this.newContactErrors : this.editContactErrors;
    delete errors[index];
  }

  addNewContact() {
    this.newContactErrors = {};
    this.selectedContacts.push({ name: "", email: "", phone: "", role: "", doNotContact: false });
  }
  removeNewContact(index: number) {
    this.selectedContacts.splice(index, 1);
    this.newContactErrors = {};
  }
  addEditContact() {
    this.editContactErrors = {};
    this.editSelectedContacts.push({
      name: "",
      email: "",
      phone: "",
      role: "",
      doNotContact: false,
    });
  }
  removeEditContact(index: number) {
    this.editSelectedContacts.splice(index, 1);
    this.editContactErrors = {};
  }

  trackByIndex(index: number): number {
    return index;
  }

  highlightText(text: string): string {
    if (!text) return "";
    if (!this.searchText || this.searchText.trim() === "") return text;
    const searchTerm = this.searchText.trim();
    const regex = new RegExp(`(${this.escapeRegex(searchTerm)})`, "gi");
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
